/**
 * FRFeatures — зубы и белок глаз.
 *
 * Обе задачи — про ОБЛАСТЬ, а не про цвет. Отбеливать «жёлтое возле рта» или
 * осветлять «светлое возле глаза» нельзя: первое зацепит губы и подбородок,
 * второе — кожу века и оправу очков. Поэтому границу задаёт геометрия
 * (контуры губ и глаз из FaceLandmarker), а внутри неё нужный пиксель
 * отбирается по признаку, который ВЫВОДИТСЯ ИЗ САМОЙ ГУБЫ этого человека в
 * этом кадре, а не задан константой.
 *
 * ── Зубы: переделка 25.09.2026 по жалобе «teeth are not any whiter» ────────
 *
 * Человек купил Премиум ради отбеливания, дважды выгрузил шестиминутный
 * ролик 1080p (1 ч 39 мин и 35 мин) — и зубы не изменились. Замер на
 * корпусе живых интервью (7 человек, 1080p, 152 кадра с открытым ртом,
 * размечены глазом: 102 «зубы видны», 22 «не видны»):
 *
 *   · прежний отбор ('lipref': доля красного R/(R+G+B) в ЛИНЕЙНОМ свете
 *     ниже середины между губой и серым) не находил зубов в 55 % кадров, где
 *     они видны. Тёплый свет и половинное цветовое разрешение видео (цвет
 *     губы «натекает» на зубы) поднимают долю красного у зуба до 0.50–0.55,
 *     а порог выходил ~0.46: ни один пиксель рта не проходил. Правило было
 *     подобрано на фотографиях; на видео оно ломалось почти всегда. Где
 *     маска всё же находилась, она нередко лежала на тёмной полости рта;
 *   · прежнее отбеливание двигало только синий к среднему R и G при
 *     неизменной сумме — светлота почти не росла (ΔL* +0.4 на 100 %), зуб
 *     становился серее, а не белее. Даже с идеальной маской: ΔWID 0.7 на
 *     100 %, заметный эффект — в 2 % кадров с зубами.
 *
 * Что заменило. Зуб отличается от губы, языка, дёсен и полости по ТРЁМ
 * относительным признакам — каждый считается от медианы кольца губы в том же
 * кадре, поэтому свет сцены, экспозиция и цвет кожи из сравнения уходят:
 *   · светлота L* по отношению к L* губы — полость рта тёмная (у кадров без зубов почти вся
 *     масса ниже 0.7);
 *   · насыщенность (C* на L*) относительно губы — губа, язык и дёсны насыщены
 *     как губа;
 *   · сдвиг цветового тона от губы — губа, язык, полость красные, зуб
 *     желтоватый; пиксели, сдвинутые к жёлтому на 10° и больше, встретились
 *     почти только в кадрах, где зубы видны.
 * Граница между «губой» и «зубом» по насыщенности и тону — диагональная
 * (чем сильнее тон ушёл к жёлтому, тем больше насыщенности допустимо),
 * поэтому это один линейный дискриминант, а не два порога. Числа ниже взяты
 * из двумерных гистограмм T- и N-кадров, а не подобраны на глаз.
 *
 * Итог на том же корпусе, 100 % (метрики не зависят от маски кандидата: эффект
 * меряется на самых светлых 20 % пикселей рта, артефакт — по всему рту
 * кадров без зубов): заметное отбеливание — 91 % кадров с зубами против 2 %,
 * ΔL* на зубах +4.3 против +0.4, ΔWID +13.6 против +0.7; губа не меняется
 * (ΔE 0.00); на кадрах без зубов средний ΔE 0.2.
 * Отвергнуто замером: «самые бесцветные из ярких» (v2) — на приоткрытом рте
 * без зубов садилось на полость и рисовало серое пятно (52 % таких кадров);
 * два независимых порога светлоты и насыщенности (v3) — пропускали сероватые
 * зубы при бледных губах (78 % против 91 %); бонус за светлоту — не дал
 * ничего. Стенд — tests/playwright/video-retouch-teeth-bench.mjs.
 */
(function (root) {
    'use strict';

    var DEG = 180 / Math.PI;

    /** Линейный sRGB → Lab (D65). */
    function lab(r, g, b, out) {
        var X = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
        var Y = r * 0.2126 + g * 0.7152 + b * 0.0722;
        var Z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
        var fx = X > 0.008856 ? Math.cbrt(X) : 7.787 * X + 16 / 116;
        var fy = Y > 0.008856 ? Math.cbrt(Y) : 7.787 * Y + 16 / 116;
        var fz = Z > 0.008856 ? Math.cbrt(Z) : 7.787 * Z + 16 / 116;
        out[0] = 116 * fy - 16; out[1] = 500 * (fx - fy); out[2] = 200 * (fy - fz);
        return out;
    }

    /** Lab (D65) → линейный sRGB. */
    function labInv(L, a, b, out) {
        var fy = (L + 16) / 116, fx = fy + a / 500, fz = fy - b / 200;
        var fi = function (t) { var t3 = t * t * t; return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787; };
        var X = fi(fx) * 0.95047, Y = fi(fy), Z = fi(fz) * 1.08883;
        out[0] = 3.2406 * X - 1.5372 * Y - 0.4986 * Z;
        out[1] = -0.9689 * X + 1.8758 * Y + 0.0415 * Z;
        out[2] = 0.0557 * X - 0.2040 * Y + 1.0570 * Z;
        return out;
    }

    function sig(x) { return 1 / (1 + Math.exp(-x)); }

    function median(arr) {
        if (!arr.length) return NaN;
        var s = Float32Array.from(arr).sort();
        return s[s.length >> 1];
    }

    /** Растеризовать контур в маску 0/1 (с необязательным сжатием к центру). */
    function regionMask(poly, w, h, scale) {
        var m = new Float32Array(w * h);
        root.FREngine.fillPoly(m, w, h, poly, 1, scale || 0);
        return m;
    }

    /**
     * Границы, выведенные из гистограмм размеченного корпуса (см. шапку).
     *   L0/sL — светлота относительно губы: середина перехода и его ширина;
     *   S0/sS — насыщенность относительно губы на нулевом сдвиге тона;
     *   H0/sH — сдвиг тона от губы к жёлтому, градусы;
     *   Smax/sSmax — потолок АБСОЛЮТНОЙ насыщенности C* / L* (26.09.2026).
     *
     * Потолок. Насыщенность сравнивается с губой, и на яркой помаде это
     * обманывает: влажный блик на ней (C* / L* 0.8–1.1) «бледнее помады» и
     * проходил как зуб — видимое светлое пятно в 2 из 21 кадра без зубов
     * корпуса (hannah_009, maricarmen_021). У зубов корпуса C* / L* 0.2–0.65 —
     * и при помаде, где её цвет «натекает» на зуб. С потолком 0.75: пятен 0
     * из 21, зубы находятся в тех же 91 % кадров, утечка в полость рта ΔE
     * 0.74 → 0.57. Потолок 0.6 резал зубы при красной помаде (hannah) —
     * отвергнут; 0.7–0.8 дают одно и то же.
     * Отвергнут и путь «бледная губа» (свет выше губы + низкая насыщенность
     * без сравнения тона): у бледных губ он находил 3 из 4 пропущенных кадров
     * с зубами, но когда MediaPipe кладёт контур отверстия рта на нижнюю губу
     * (maria_003) — белил саму губу, а на кромке губы рисовал линии. Отличить
     * зуб от бледной губы по цвету нельзя; нужен другой признак.
     */
    var TEETH = { L0: 0.72, sL: 0.05, S0: 0.75, sS: 0.08, H0: 2, sH: 6, Smax: 0.75, sSmax: 0.05 };

    /**
     * Эталон губы: медианы светлоты, относительной насыщенности и тона по
     * кольцу между внешним контуром губ и (расширенным) отверстием рта.
     * null — губы видно слишком мало, чтобы на неё опираться.
     */
    function lipReference(pl, lips, mouth) {
        if (!lips || lips.length < 3 || !mouth) return null;
        var w = pl.w, h = pl.h, n = w * h;
        var band = regionMask(lips, w, h, 0.97);
        var hole = regionMask(mouth, w, h, 1.12);
        var Ls = [], Ss = [], As = [], Bs = [], c = [0, 0, 0];
        for (var i = 0; i < n; i++) {
            if (band[i] < 0.5 || hole[i] > 0.5) continue;
            lab(pl.R[i], pl.G[i], pl.B[i], c);
            Ls.push(c[0]); Ss.push(Math.sqrt(c[1] * c[1] + c[2] * c[2]) / Math.max(4, c[0]));
            As.push(c[1]); Bs.push(c[2]);
        }
        if (Ls.length < 30) return null;
        var L = median(Ls), S = median(Ss);
        if (!(L > 2) || !(S > 0.02)) return null;
        return { L: L, S: S, hue: Math.atan2(median(Bs), median(As)), n: Ls.length };
    }

    /**
     * Зубы внутри отверстия рта — МЯГКАЯ маска 0..1 (принадлежность пикселя).
     * @param {Array} lips   внешний контур губ (face.lips)
     * @param {Array} mouth  отверстие рта (face.mouthOpen)
     */
    function teethMask(pl, lips, mouth, ref) {
        var w = pl.w, h = pl.h, n = w * h;
        var out = new Float32Array(n);
        if (!mouth) return out;
        ref = ref || lipReference(pl, lips, mouth);
        if (!ref) return out;                      // губы не видно — не с чем сравнивать
        // Небольшое сжатие: край отверстия — это ещё кайма губы.
        var inner = regionMask(mouth, w, h, 0.97);
        var c = [0, 0, 0], T = TEETH, any = 0;
        for (var i = 0; i < n; i++) {
            if (inner[i] < 0.5) continue;
            lab(pl.R[i], pl.G[i], pl.B[i], c);
            var S = Math.sqrt(c[1] * c[1] + c[2] * c[2]) / Math.max(4, c[0]);
            var dh = (Math.atan2(c[2], c[1]) - ref.hue) * DEG;
            if (dh > 180) dh -= 360; else if (dh < -180) dh += 360;
            var z = (T.S0 - S / ref.S) / T.sS + (dh - T.H0) / T.sH;
            out[i] = sig((c[0] / ref.L - T.L0) / T.sL) * sig(z) * sig((T.Smax - S) / T.sSmax);
            any++;
        }
        if (!any) return out;
        // Мягкий край — жёсткая граница отбеливания видна как наклейка; но
        // размытие не должно растаскивать зуб на соседнюю полость: половина
        // размытого минимума и половина исходной принадлежности.
        var soft = (root.FREngine.boxBlurSparse || root.FREngine.boxBlur)(out, w, h, 1);
        for (var q = 0; q < n; q++) {
            soft[q] = inner[q] > 0.5 ? 0.5 * Math.min(out[q], soft[q]) + 0.5 * out[q] : 0;
        }
        return soft;
    }

    /**
     * Силы отбеливания на 100 %. Свет — как экспозиция (умножение
     * отражённого света: тени между зубами сохраняют соотношение, зуб не
     * становится плоской плашкой) с «коленом» у белого — выбитый в бумагу зуб
     * читается как вставная челюсть. Цвет — снимается доля жёлтого (b*) и
     * розового подтона (a*), а не весь цвет.
     */
    var WHITEN = { gain: 0.35, knee: 88, yellow: 0.8, pink: 0.5, bFloor: 1 };

    /**
     * Отбелить зубы. Возвращает новые плоскости + teethPx (сколько пикселей
     * зубов нашлось — тул говорит человеку, видны ли зубы на этом кадре).
     */
    function whitenTeeth(pl, faces, p) {
        p = p || {};
        var amount = p.amount === undefined ? 0 : p.amount;
        var w = pl.w, h = pl.h, n = w * h;
        var out = {
            w: w, h: h, clip: pl.clip,
            R: Float32Array.from(pl.R), G: Float32Array.from(pl.G), B: Float32Array.from(pl.B),
        };
        out.teethPx = 0;
        if (amount <= 0 || !faces || !faces.length) return out;

        var total = new Float32Array(n), found = 0;
        for (var f = 0; f < faces.length; f++) {
            var mouth = faces[f].mouthOpen;
            if (!mouth) continue;      // рот не найден — белить нечего
            var m = teethMask(pl, faces[f].lips, mouth);
            for (var i = 0; i < n; i++) if (m[i] > total[i]) total[i] = m[i];
        }
        for (var c0 = 0; c0 < n; c0++) if (total[c0] > 0.5) found++;
        out.teethPx = found;
        if (!found) return out;

        var W = WHITEN, c = [0, 0, 0], rgb = [0, 0, 0];
        for (var k = 0; k < n; k++) {
            var a = total[k] * amount;
            if (a <= 0.004) continue;
            lab(pl.R[k], pl.G[k], pl.B[k], c);
            var L = c[0];
            var Y = Math.pow((L + 16) / 116, 3) * (1 + W.gain * a);
            var L2 = 116 * Math.cbrt(Y < 1 ? Y : 1) - 16;
            if (L2 > W.knee) L2 = W.knee + (L2 - W.knee) * (100 - W.knee) / (100 - W.knee + (L2 - W.knee));
            if (L2 > L) L = L2;
            var A = c[1], B = c[2];
            if (B > W.bFloor) B -= (B - W.bFloor) * W.yellow * a;
            if (A > 0) A -= A * W.pink * a;
            labInv(L, A, B, rgb);
            out.R[k] = rgb[0] < 0 ? 0 : (rgb[0] > 1 ? 1 : rgb[0]);
            out.G[k] = rgb[1] < 0 ? 0 : (rgb[1] > 1 ? 1 : rgb[1]);
            out.B[k] = rgb[2] < 0 ? 0 : (rgb[2] > 1 ? 1 : rgb[2]);
        }
        return out;
    }

    /**
     * Осветлить белок глаза (переделано 25.09.2026 вместе с зубами).
     *
     * Прежняя версия работала по контуру-обрывку (нижнее веко с хордой) и
     * отбирала «самые светлые 45 %» — радужка отсекалась лишь квантилем, а
     * светлота почти не росла (подъём 14 % в линейном свете против снятия
     * красного). Замер на 47 кадрах живых интервью, 100 %: ΔL* белка +0.3,
     * заметно — ни в одном кадре; у части лиц белок даже темнел.
     *
     * Теперь: область — ПОЛНЫЙ контур глаза минус диск радужки по её
     * собственным точкам (центр + край, 478-точечная модель), а внутри —
     * мягкая принадлежность по относительной светлоте (ресницы и тень века
     * тёмные) и низкой насыщенности (слёзное мясцо и кожа века насыщены).
     * Правка — в Lab: свет как экспозиция (+25 % на 100 %, «колено» у белого —
     * светящийся белок узнаётся как фильтр сразу), снимается 60 % красноты и
     * 40 % желтизны. Итог на том же корпусе: ΔL* белка +2.0 (было +0.3),
     * радужка ΔE 0.00 (было 0.20 — прежний квантиль её задевал), веко 0.00.
     */
    var SCLERA = { scale: 0.92, iris: 1.12, L0: 0.62, sL: 0.06, S0: 0.32, sS: 0.05 };
    var EYE_WHITE = { gain: 0.25, knee: 90, red: 0.6, yellow: 0.4 };

    function irisDisc(pts, ring, w, h) {
        if (!pts || !ring || !pts[ring[0]]) return null;
        var c = pts[ring[0]], cx = c.x * w, cy = c.y * h, r = 0, k = 0;
        for (var i = 1; i < ring.length; i++) {
            var p = pts[ring[i]]; if (!p) continue;
            r += Math.sqrt((p.x * w - cx) * (p.x * w - cx) + (p.y * h - cy) * (p.y * h - cy)); k++;
        }
        return k ? { x: cx, y: cy, r: r / k } : null;
    }

    function scleraMask(pl, fc) {
        var w = pl.w, h = pl.h, n = w * h, out = new Float32Array(n);
        var L = root.FRLandmarks || {}, S = SCLERA, c = [0, 0, 0];
        [[fc.leftEye, L.LEFT_IRIS], [fc.rightEye, L.RIGHT_IRIS]].forEach(function (pair) {
            var eye = pair[0];
            if (!eye || eye.length < 4) return;
            var region = regionMask(eye, w, h, S.scale);
            var iris = irisDisc(fc.pts, pair[1], w, h);
            var ir = iris ? iris.r * S.iris : 0;
            var idx = [], Ls = [], Ss = [];
            for (var i = 0; i < n; i++) {
                if (region[i] < 0.5) continue;
                if (iris) {
                    var dx = (i % w) + 0.5 - iris.x, dy = ((i / w) | 0) + 0.5 - iris.y;
                    if (dx * dx + dy * dy <= ir * ir) continue;
                }
                lab(pl.R[i], pl.G[i], pl.B[i], c);
                idx.push(i); Ls.push(c[0]); Ss.push(Math.sqrt(c[1] * c[1] + c[2] * c[2]) / Math.max(4, c[0]));
            }
            if (idx.length < 8) return;
            var srt = Float32Array.from(Ls).sort();
            var L90 = srt[Math.min(srt.length - 1, Math.floor(srt.length * 0.9))];
            if (!(L90 > 2)) return;
            for (var k = 0; k < idx.length; k++) {
                var p = sig((Ls[k] / L90 - S.L0) / S.sL) * sig((S.S0 - Ss[k]) / S.sS);
                if (p > out[idx[k]]) out[idx[k]] = p;
            }
        });
        var soft = (root.FREngine.boxBlurSparse || root.FREngine.boxBlur)(out, w, h, 1);
        for (var q = 0; q < n; q++) soft[q] = 0.5 * Math.min(out[q], soft[q]) + 0.5 * out[q];
        return soft;
    }

    function brightenEyes(pl, faces, p) {
        p = p || {};
        var amount = p.amount === undefined ? 0 : p.amount;
        var w = pl.w, h = pl.h, n = w * h;
        var out = {
            w: w, h: h, clip: pl.clip,
            R: Float32Array.from(pl.R), G: Float32Array.from(pl.G), B: Float32Array.from(pl.B),
        };
        out.scleraPx = 0;
        if (amount <= 0 || !faces || !faces.length) return out;

        var mask = new Float32Array(n), px = 0;
        for (var f = 0; f < faces.length; f++) {
            var m = scleraMask(pl, faces[f]);
            for (var i = 0; i < n; i++) if (m[i] > mask[i]) mask[i] = m[i];
        }
        for (var j = 0; j < n; j++) if (mask[j] > 0.5) px++;
        out.scleraPx = px;
        if (!px) return out;

        var W = EYE_WHITE, c = [0, 0, 0], rgb = [0, 0, 0];
        for (var z = 0; z < n; z++) {
            var a = mask[z] * amount;
            if (a <= 0.004) continue;
            lab(pl.R[z], pl.G[z], pl.B[z], c);
            var L = c[0];
            var Y = Math.pow((L + 16) / 116, 3) * (1 + W.gain * a);
            var L2 = 116 * Math.cbrt(Y < 1 ? Y : 1) - 16;
            if (L2 > W.knee) L2 = W.knee + (L2 - W.knee) * (100 - W.knee) / (100 - W.knee + (L2 - W.knee));
            if (L2 > L) L = L2;
            var A = c[1], B = c[2];
            if (A > 0) A -= A * W.red * a;
            if (B > 0) B -= B * W.yellow * a;
            labInv(L, A, B, rgb);
            out.R[z] = rgb[0] < 0 ? 0 : (rgb[0] > 1 ? 1 : rgb[0]);
            out.G[z] = rgb[1] < 0 ? 0 : (rgb[1] > 1 ? 1 : rgb[1]);
            out.B[z] = rgb[2] < 0 ? 0 : (rgb[2] > 1 ? 1 : rgb[2]);
        }
        return out;
    }

    root.FRFeatures = {
        whitenTeeth: whitenTeeth, brightenEyes: brightenEyes,
        teethMask: teethMask, lipReference: lipReference, regionMask: regionMask, scleraMask: scleraMask,
        TEETH: TEETH, WHITEN: WHITEN, SCLERA: SCLERA, EYE_WHITE: EYE_WHITE,
    };
})(typeof window !== 'undefined' ? window : globalThis);
